import { Fragment } from "preact";
import { useContext } from "preact/hooks";
import { Text } from "preact-i18n";

import { Configs } from "../../helpers/context";
import { formatIntegerCurrencyString } from "../../helpers/formatters";
import { toNegativeValue } from "../../helpers/order";

const OrderSummaryLineItem = ({ lineItem, isCheckoutPage = false, isLast = false }) => {
  const configsContext = useContext(Configs);
  const lineItemAmount = (lineItem.unitAmount?.value || 0) * (lineItem.quantity || 1);
  const fees = (lineItem && lineItem.fees ? lineItem.fees : []).filter(
    (fee) =>
      fee.appliedBeforeTax || fee.appliedBeforeTax === undefined || !("appliedBeforeTax" in fee),
  );
  const discounts = (lineItem && lineItem.discounts ? lineItem.discounts : [])
    .filter(
      (discount) =>
        discount.appliedBeforeTax ||
        discount.appliedBeforeTax === undefined ||
        !("appliedBeforeTax" in discount),
    )
    .map(toNegativeValue);
  const taxes = lineItem && lineItem.taxes ? lineItem.taxes : [];
  const adjustments = [].concat(fees, discounts, taxes);

  const renderAdjustmentRow = (adjustment) => {
    const isPercent = adjustment.ratePercentage && adjustment.ratePercentage !== "0";

    return (
      <div className={isCheckoutPage ? "item-adjustments-row-checkout" : "item-adjustments-row"}>
        <span>
          {adjustment.name} {isPercent ? `(${adjustment.ratePercentage}%)` : ""}
        </span>
        <span className="order-quantity" />
        {!isCheckoutPage && <span className="order-unit price" />}
        <span className="item-adjustments-amount">
          {formatIntegerCurrencyString(
            adjustment.amount.value,
            configsContext?.configs?.currency,
            configsContext?.configs?.ecommerceLocaleLanguage,
          )}
        </span>
      </div>
    );
  };

  return (
    <Fragment>
      <div className={isCheckoutPage ? "order-description-checkout" : "order-description"}>
        <span className="order-title">{lineItem.name}</span>
        <span className="order-quantity order-text-center">{lineItem.quantity}</span>
        {!isCheckoutPage && (
          <span className="order-unit price order-text-right">
            {lineItem.unitAmount?.value
              ? formatIntegerCurrencyString(
                  lineItem.unitAmount.value,
                  configsContext?.configs?.currency,
                  configsContext?.configs?.ecommerceLocaleLanguage,
                )
              : ""}
          </span>
        )}
        <span className="cart-size">
          {formatIntegerCurrencyString(
            lineItemAmount,
            configsContext?.configs?.currency,
            configsContext?.configs?.ecommerceLocaleLanguage,
          )}
        </span>
      </div>
      <div className="item-adjustments">
        {adjustments.map((adjustment) => renderAdjustmentRow(adjustment))}
      </div>
      <div style="clear: both;"></div>
      {lineItem.unitAmount?.value && !isCheckoutPage ? (
        <div className="mobile-price">
          <br />
          <div>
            <span>
              <Text id="ORDER_ITEM_UNIT_PRICE">Unit Price: </Text>
            </span>
            <span>
              {lineItem.unitAmount?.value
                ? formatIntegerCurrencyString(
                    lineItem.unitAmount.value,
                    configsContext?.configs?.currency,
                    configsContext?.configs?.ecommerceLocaleLanguage,
                  )
                : ""}
            </span>
          </div>
        </div>
      ) : null}
      {lineItem.notes ? (
        <Fragment>
          <div className="order-note">
            {lineItem.notes
              .filter((note) => note.authorType === "CUSTOMER" && note.content.trim() !== "")
              .map((note, index) => (
                <div key={index}>
                  {index === 0 ? (
                    <div>
                      <span className="order-note-title">
                        <Text id="ORDER_ITEM_NOTE">Note: </Text>
                      </span>
                      <span>{note.content}</span>
                    </div>
                  ) : (
                    <div>
                      <span>{note.content}</span>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </Fragment>
      ) : null}
      {!isLast && <div className="order-summary-divider-dashed"></div>}
      {lineItem.details?.selectedOptions?.length ? (
        <div className="order-modifiers">
          {lineItem.details?.selectedOptions.map((option, i) => {
            return (
              <span className="modifiers">
                {i ? " | " : ""}
                {option.attribute}: {option?.values?.join(", ")}
              </span>
            );
          })}
        </div>
      ) : null}
      {lineItem.details?.selectedAddons?.length ? (
        <div className="order-modifiers">
          {lineItem.details?.selectedAddons.map((addon, i) => {
            return (
              <span className="modifiers">
                {i ? " | " : ""}
                {addon.attribute}:{" "}
                {addon?.values
                  ?.map(
                    (value) =>
                      `${value.name}${
                        value.costAdjustment?.value
                          ? ` (+${formatIntegerCurrencyString(
                              value.costAdjustment.value,
                              configsContext?.configs?.currency,
                              configsContext?.configs?.ecommerceLocaleLanguage,
                            )})`
                          : ""
                      }`,
                  )
                  .join(", ")}
              </span>
            );
          })}
        </div>
      ) : null}
    </Fragment>
  );
};

export default OrderSummaryLineItem;
